import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
  closeVirtualAccount,
  createApplicationContact,
  createVirtualAccount,
  deleteApplicationContact,
  deleteUrlLimit,
  deleteUrlPermission,
  fetchApplicationContacts,
  fetchUrlLimits,
  fetchUrlPermissions,
  fetchVirtualAccounts,
  fetchWallet,
  setApiSecret,
  setTransactionPassword,
  updateExternalApplication,
  updateApplicationContact,
  upsertUrlLimit,
  upsertUrlPermission,
} from "../../Apis/Wallet-api";
import { formatLabel, formatMoney } from "../../Utils/walletFormat";
import { isWalletAgentUser } from "../../Utils/walletAdminAccess";
import useAuthStore from "../../Zustand/Store/useAuthStore";

const TAB_DEFINITIONS = [
  { key: "overview", label: "Overview" },
  { key: "url-access", label: "URL access" },
  { key: "virtual-accounts", label: "Virtual accounts" },
  { key: "contacts", label: "Contacts" },
];

const AGENT_TAB_KEYS = new Set(["overview", "contacts"]);

const ENDPOINT_OPTIONS = [
  "balance",
  "credit",
  "debit",
  "refund",
  "recharge",
  "ledger",
  "transactions",
  "otp",
  "virtual_account",
];

const CONTACT_TYPES = ["primary", "finance", "technical"];

const NOTIFY_EVENTS = [
  "recharge",
  "debit",
  "credit",
  "refund",
  "payment_success",
  "payment_failure",
];

const EMPTY_CONTACT_FORM = {
  id: null,
  type: "finance",
  email: "",
  phone: "",
  notifyAll: true,
  notify_on_events: [],
};

function StatCard({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 140 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" sx={{ mt: 0.5 }}>{value}</Typography>
    </Paper>
  );
}

export default function WalletDetailPage() {
  const { walletId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuthStore();
  const isAgentUser = isWalletAgentUser(userData);
  const visibleTabs = isAgentUser
    ? TAB_DEFINITIONS.filter((tabDef) => AGENT_TAB_KEYS.has(tabDef.key))
    : TAB_DEFINITIONS;

  const [tabKey, setTabKey] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const appName = location.state?.appName || "";
  const externalAppId =
    location.state?.externalAppId || wallet?.external_application_id;

  const [permissions, setPermissions] = useState([]);
  const [limits, setLimits] = useState([]);
  const [virtualAccounts, setVirtualAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [permDialog, setPermDialog] = useState({ open: false, endpoint: "balance", allowed: true });
  const [limitDialog, setLimitDialog] = useState({
    open: false,
    endpoint: "debit",
    max_amount_per_txn: "",
    max_daily_amount: "",
    max_daily_count: "",
  });
  const [contactDialog, setContactDialog] = useState({
    open: false,
    saving: false,
    form: EMPTY_CONTACT_FORM,
  });
  const [txnPwDialog, setTxnPwDialog] = useState({
    open: false,
    mode: "generate",
    password: "",
    confirm: "",
  });
  const [txnPwResult, setTxnPwResult] = useState(null);
  const [txnPwSaving, setTxnPwSaving] = useState(false);
  const [apiSecretDialog, setApiSecretDialog] = useState({
    open: false,
    mode: "generate",
    secret: "",
    confirm: "",
  });
  const [apiSecretResult, setApiSecretResult] = useState(null);
  const [apiSecretSaving, setApiSecretSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  const loadWallet = useCallback(async () => {
    setLoading(true);
    try {
      setWallet(await fetchWallet(walletId));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load wallet.");
    } finally {
      setLoading(false);
    }
  }, [walletId]);

  const loadTabData = useCallback(async () => {
    if (!externalAppId) return;
    try {
      if (tabKey === "url-access") {
        setPermissions(await fetchUrlPermissions(externalAppId));
        setLimits(await fetchUrlLimits(externalAppId));
      } else if (tabKey === "virtual-accounts") {
        setVirtualAccounts(await fetchVirtualAccounts(externalAppId));
      } else if (tabKey === "contacts") {
        setContacts(await fetchApplicationContacts(externalAppId));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load tab data.");
    }
  }, [tabKey, externalAppId]);

  useEffect(() => {
    if (!visibleTabs.some((tabDef) => tabDef.key === tabKey)) {
      setTabKey("overview");
    }
  }, [visibleTabs, tabKey]);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  useEffect(() => {
    if (wallet && tabKey !== "overview") loadTabData();
  }, [wallet, tabKey, loadTabData]);

  const toggleApplicationActive = async () => {
    if (!externalAppId || !wallet) return;
    const nextStatus = wallet.status === "active" ? "deactivated" : "active";
    setStatusSaving(true);
    try {
      const updated = await updateExternalApplication(externalAppId, {
        status: nextStatus,
      });
      setWallet((prev) =>
        prev ? { ...prev, status: updated?.status ?? nextStatus } : prev
      );
      toast.success(
        nextStatus === "active" ? "Application activated." : "Application deactivated."
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Status update failed.");
    } finally {
      setStatusSaving(false);
    }
  };

  if (loading && !wallet) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!wallet) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Wallet not found.</Typography>
        <Button onClick={() => navigate("/wallet/applications")}>Back</Button>
      </Box>
    );
  }

  const currency = wallet.currency || "INR";

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate("/wallet/applications")} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5">{appName || `Wallet #${wallet.wallet_id}`}</Typography>
          <Typography variant="body2" color="text.secondary">
            Wallet {wallet.wallet_id} · App {wallet.external_application_id}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            size="small"
            label={formatLabel(wallet.status || "unknown")}
            color={
              wallet.status === "active"
                ? "success"
                : wallet.status === "suspended"
                  ? "warning"
                  : "default"
            }
          />
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Switch
                size="small"
                checked={wallet.status === "active"}
                disabled={statusSaving || !externalAppId}
                onChange={toggleApplicationActive}
              />
            }
            label="Active"
          />
        </Stack>
        <IconButton onClick={loadWallet} size="small">
          <RefreshIcon />
        </IconButton>
      </Stack>

      <Tabs value={tabKey} onChange={(_, v) => setTabKey(v)} sx={{ mb: 2 }}>
        {visibleTabs.map((tabDef) => (
          <Tab key={tabDef.key} value={tabDef.key} label={tabDef.label} />
        ))}
      </Tabs>

      {tabKey === "overview" && (
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <StatCard label="Balance" value={formatMoney(wallet.balance, currency)} />
            <StatCard label="Available" value={formatMoney(wallet.available_to_spend, currency)} />
            <StatCard
              label="Credit used / limit"
              value={`${formatMoney(wallet.credit_used, currency)} / ${formatMoney(wallet.credit_limit, currency)}`}
            />
          </Stack>
         

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Agent credentials
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Use these headers for agent API calls. Secret and transaction password
              are never shown here after create/rotate.
            </Typography>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  API key (<code>X-Api-Key</code>)
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    sx={{ wordBreak: "break-all", flex: 1 }}
                  >
                    {wallet.api_key || "—"}
                  </Typography>
                  {wallet.api_key && (
                    <Button
                      size="small"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(wallet.api_key);
                          toast.success("API key copied.");
                        } catch {
                          toast.error("Could not copy.");
                        }
                      }}
                    >
                      Copy
                    </Button>
                  )}
                </Stack>
              </Box>
              <Typography variant="caption" color="text.secondary">
                API secret (<code>X-Api-Secret</code>) — set / rotate below
              </Typography>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Transaction password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Used by the agent API for debit / recharge / refund step-up (not login).
              Plaintext is shown only once after set or rotate.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setTxnPwResult(null);
                setTxnPwDialog({
                  open: true,
                  mode: "generate",
                  password: "",
                  confirm: "",
                });
              }}
            >
              Set / rotate password
            </Button>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              API secret
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Used with <code>X-Api-Secret</code> for agent authentication.
              Plaintext is shown only once after set or rotate. API key stays the
              same.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setApiSecretResult(null);
                setApiSecretDialog({
                  open: true,
                  mode: "generate",
                  secret: "",
                  confirm: "",
                });
              }}
            >
              Set / rotate API secret
            </Button>
          </Paper>
        </Stack>
      )}

      {tabKey === "url-access" && (
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">URL permissions</Typography>
              <Button
                size="small"
                onClick={() => setPermDialog({ open: true, endpoint: "balance", allowed: true })}
              >
                Add / edit
              </Button>
            </Stack>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Endpoint</TableCell>
                    <TableCell>Allowed</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {permissions.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{formatLabel(p.endpoint)}</TableCell>
                      <TableCell>{p.allowed ? "Yes" : "No"}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          onClick={async () => {
                            await deleteUrlPermission(externalAppId, p.endpoint);
                            setPermissions(await fetchUrlPermissions(externalAppId));
                          }}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">URL limits</Typography>
              <Button
                size="small"
                onClick={() =>
                  setLimitDialog({
                    open: true,
                    endpoint: "debit",
                    max_amount_per_txn: "",
                    max_daily_amount: "",
                    max_daily_count: "",
                  })
                }
              >
                Add / edit
              </Button>
            </Stack>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Endpoint</TableCell>
                    <TableCell>Max / txn</TableCell>
                    <TableCell>Max daily amt</TableCell>
                    <TableCell>Max daily count</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {limits.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{formatLabel(l.endpoint)}</TableCell>
                      <TableCell>{l.max_amount_per_txn ?? "—"}</TableCell>
                      <TableCell>{l.max_daily_amount ?? "—"}</TableCell>
                      <TableCell>{l.max_daily_count ?? "—"}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          onClick={async () => {
                            await deleteUrlLimit(externalAppId, l.endpoint);
                            setLimits(await fetchUrlLimits(externalAppId));
                          }}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
      )}

      {tabKey === "virtual-accounts" && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={async () => {
                try {
                  await createVirtualAccount(externalAppId);
                  setVirtualAccounts(await fetchVirtualAccounts(externalAppId));
                  toast.success("Virtual account created.");
                } catch (err) {
                  toast.error(err.response?.data?.message || "Create failed.");
                }
              }}
            >
              Create VA
            </Button>
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account number</TableCell>
                  <TableCell>IFSC</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {virtualAccounts.map((va) => (
                  <TableRow key={va.id}>
                    <TableCell>
                      <Typography fontFamily="monospace" variant="body2">
                        {va.virtual_account_number}
                      </Typography>
                    </TableCell>
                    <TableCell>{va.ifsc_code}</TableCell>
                    <TableCell><Chip size="small" label={formatLabel(va.status)} /></TableCell>
                    <TableCell align="right">
                      {va.status === "active" && (
                        <Button
                          size="small"
                          color="warning"
                          onClick={async () => {
                            await closeVirtualAccount(externalAppId, va.id);
                            setVirtualAccounts(await fetchVirtualAccounts(externalAppId));
                            toast.success("Virtual account closed.");
                          }}
                        >
                          Close
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tabKey === "contacts" && (
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined">
            Contacts receive wallet notifications (SMS / WhatsApp / email) and
            OTP delivery for step-up auth. Empty event list = subscribe to all
            events.
          </Alert>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              variant="contained"
              onClick={() =>
                setContactDialog({
                  open: true,
                  saving: false,
                  form: { ...EMPTY_CONTACT_FORM },
                })
              }
            >
              Add contact
            </Button>
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Notify on</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No contacts configured. Primary contact is created when
                        the agent is provisioned.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  contacts.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Chip size="small" label={row.type} />
                      </TableCell>
                      <TableCell>{row.email || "—"}</TableCell>
                      <TableCell>{row.phone || "—"}</TableCell>
                      <TableCell>
                        {!row.notify_on_events?.length ? (
                          <Typography variant="caption">All events</Typography>
                        ) : (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {row.notify_on_events.map((ev) => (
                              <Chip
                                key={ev}
                                size="small"
                                label={formatLabel(ev)}
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            onClick={() =>
                              setContactDialog({
                                open: true,
                                saving: false,
                                form: {
                                  id: row.id,
                                  type: row.type,
                                  email: row.email || "",
                                  phone: row.phone || "",
                                  notifyAll: !row.notify_on_events?.length,
                                  notify_on_events: row.notify_on_events || [],
                                },
                              })
                            }
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={async () => {
                              const label = row.email || row.phone || row.type;
                              if (
                                !window.confirm(
                                  `Delete ${row.type} contact (${label})?`
                                )
                              ) {
                                return;
                              }
                              try {
                                await deleteApplicationContact(
                                  externalAppId,
                                  row.id
                                );
                                setContacts(
                                  await fetchApplicationContacts(externalAppId)
                                );
                                toast.success("Contact deleted.");
                              } catch (err) {
                                toast.error(
                                  err.response?.data?.message || "Delete failed."
                                );
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      <Dialog open={permDialog.open} onClose={() => setPermDialog({ ...permDialog, open: false })}>
        <DialogTitle>URL permission</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="dense"
            label="Endpoint"
            value={permDialog.endpoint}
            onChange={(e) => setPermDialog({ ...permDialog, endpoint: e.target.value })}
            SelectProps={{ native: true }}
          >
            {ENDPOINT_OPTIONS.map((ep) => (
              <option key={ep} value={ep}>{formatLabel(ep)}</option>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            margin="dense"
            label="Allowed"
            value={permDialog.allowed ? "1" : "0"}
            onChange={(e) =>
              setPermDialog({ ...permDialog, allowed: e.target.value === "1" })
            }
            SelectProps={{ native: true }}
          >
            <option value="1">Yes</option>
            <option value="0">No</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermDialog({ ...permDialog, open: false })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              await upsertUrlPermission(externalAppId, permDialog.endpoint, permDialog.allowed);
              setPermDialog({ ...permDialog, open: false });
              setPermissions(await fetchUrlPermissions(externalAppId));
              toast.success("Permission saved.");
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={limitDialog.open} onClose={() => setLimitDialog({ ...limitDialog, open: false })}>
        <DialogTitle>URL limit</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="dense"
            label="Endpoint"
            value={limitDialog.endpoint}
            onChange={(e) => setLimitDialog({ ...limitDialog, endpoint: e.target.value })}
            SelectProps={{ native: true }}
          >
            {["credit", "debit", "refund", "recharge"].map((ep) => (
              <option key={ep} value={ep}>{formatLabel(ep)}</option>
            ))}
          </TextField>
          <TextField
            fullWidth
            margin="dense"
            label="Max amount per txn"
            value={limitDialog.max_amount_per_txn}
            onChange={(e) =>
              setLimitDialog({ ...limitDialog, max_amount_per_txn: e.target.value })
            }
          />
          <TextField
            fullWidth
            margin="dense"
            label="Max daily amount"
            value={limitDialog.max_daily_amount}
            onChange={(e) =>
              setLimitDialog({ ...limitDialog, max_daily_amount: e.target.value })
            }
          />
          <TextField
            fullWidth
            margin="dense"
            label="Max daily count"
            value={limitDialog.max_daily_count}
            onChange={(e) =>
              setLimitDialog({ ...limitDialog, max_daily_count: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitDialog({ ...limitDialog, open: false })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              await upsertUrlLimit(externalAppId, {
                endpoint: limitDialog.endpoint,
                max_amount_per_txn: limitDialog.max_amount_per_txn || null,
                max_daily_amount: limitDialog.max_daily_amount || null,
                max_daily_count: limitDialog.max_daily_count || null,
              });
              setLimitDialog({ ...limitDialog, open: false });
              setLimits(await fetchUrlLimits(externalAppId));
              toast.success("Limit saved.");
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={contactDialog.open}
        onClose={() => {
          if (!contactDialog.saving) {
            setContactDialog({ open: false, saving: false, form: EMPTY_CONTACT_FORM });
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {contactDialog.form.id ? "Edit contact" : "Add contact"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="contact-type-label">Type</InputLabel>
              <Select
                labelId="contact-type-label"
                label="Type"
                value={contactDialog.form.type}
                disabled={Boolean(contactDialog.form.id)}
                onChange={(e) =>
                  setContactDialog({
                    ...contactDialog,
                    form: { ...contactDialog.form, type: e.target.value },
                  })
                }
              >
                {CONTACT_TYPES.filter(
                  (t) =>
                    t === contactDialog.form.type ||
                    !contacts.some((c) => c.type === t)
                ).map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Email"
              type="email"
              value={contactDialog.form.email}
              onChange={(e) =>
                setContactDialog({
                  ...contactDialog,
                  form: { ...contactDialog.form, email: e.target.value },
                })
              }
              helperText={
                contactDialog.form.type === "primary"
                  ? "Required for primary contact"
                  : "Email or phone required"
              }
            />
            <TextField
              fullWidth
              size="small"
              label="Phone"
              value={contactDialog.form.phone}
              onChange={(e) =>
                setContactDialog({
                  ...contactDialog,
                  form: { ...contactDialog.form, phone: e.target.value },
                })
              }
            />
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contactDialog.form.notifyAll}
                    onChange={(e) =>
                      setContactDialog({
                        ...contactDialog,
                        form: {
                          ...contactDialog.form,
                          notifyAll: e.target.checked,
                          notify_on_events: e.target.checked
                            ? []
                            : [...NOTIFY_EVENTS],
                        },
                      })
                    }
                  />
                }
                label="Notify on all wallet events"
              />
              {!contactDialog.form.notifyAll && (
                <FormGroup row sx={{ pl: 1 }}>
                  {NOTIFY_EVENTS.map((ev) => (
                    <FormControlLabel
                      key={ev}
                      control={
                        <Checkbox
                          size="small"
                          checked={contactDialog.form.notify_on_events.includes(ev)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...contactDialog.form.notify_on_events, ev]
                              : contactDialog.form.notify_on_events.filter(
                                  (x) => x !== ev
                                );
                            setContactDialog({
                              ...contactDialog,
                              form: {
                                ...contactDialog.form,
                                notify_on_events: next,
                              },
                            });
                          }}
                        />
                      }
                      label={formatLabel(ev)}
                    />
                  ))}
                </FormGroup>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={contactDialog.saving}
            onClick={() =>
              setContactDialog({ open: false, saving: false, form: EMPTY_CONTACT_FORM })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={contactDialog.saving}
            onClick={async () => {
              const { id, type, email, phone, notifyAll, notify_on_events } =
                contactDialog.form;
              const payload = {
                type,
                email: email.trim() || null,
                phone: phone.trim() || null,
                notify_on_events: notifyAll ? null : notify_on_events,
              };
              setContactDialog({ ...contactDialog, saving: true });
              try {
                if (id) {
                  await updateApplicationContact(externalAppId, id, payload);
                  toast.success("Contact updated.");
                } else {
                  await createApplicationContact(externalAppId, payload);
                  toast.success("Contact saved.");
                }
                setContactDialog({
                  open: false,
                  saving: false,
                  form: EMPTY_CONTACT_FORM,
                });
                setContacts(await fetchApplicationContacts(externalAppId));
              } catch (err) {
                toast.error(err.response?.data?.message || "Save failed.");
                setContactDialog({ ...contactDialog, saving: false });
              }
            }}
          >
            {contactDialog.saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={txnPwDialog.open}
        onClose={() => {
          if (!txnPwSaving) {
            setTxnPwDialog({ ...txnPwDialog, open: false });
            setTxnPwResult(null);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {txnPwResult
            ? "Transaction password updated"
            : "Set / rotate transaction password"}
        </DialogTitle>
        <DialogContent>
          {txnPwResult ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">
                Copy this password now — it will not be shown again.
              </Alert>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  sx={{ flex: 1, wordBreak: "break-all" }}
                >
                  {txnPwResult.transaction_password}
                </Typography>
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        txnPwResult.transaction_password
                      );
                      toast.success("Copied.");
                    } catch {
                      toast.error("Could not copy.");
                    }
                  }}
                >
                  Copy
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Mode"
                value={txnPwDialog.mode}
                onChange={(e) =>
                  setTxnPwDialog({
                    ...txnPwDialog,
                    mode: e.target.value,
                    password: "",
                    confirm: "",
                  })
                }
                SelectProps={{ native: true }}
              >
                <option value="generate">Auto-generate secure password</option>
                <option value="custom">Set a custom password</option>
              </TextField>
              {txnPwDialog.mode === "custom" && (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="New password (min 8 chars)"
                    value={txnPwDialog.password}
                    onChange={(e) =>
                      setTxnPwDialog({ ...txnPwDialog, password: e.target.value })
                    }
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Confirm password"
                    value={txnPwDialog.confirm}
                    onChange={(e) =>
                      setTxnPwDialog({ ...txnPwDialog, confirm: e.target.value })
                    }
                  />
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={txnPwSaving}
            onClick={() => {
              setTxnPwDialog({ ...txnPwDialog, open: false });
              setTxnPwResult(null);
            }}
          >
            {txnPwResult ? "Close" : "Cancel"}
          </Button>
          {!txnPwResult && (
            <Button
              variant="contained"
              disabled={txnPwSaving}
              onClick={async () => {
                if (!externalAppId) {
                  toast.error("External application id missing.");
                  return;
                }
                if (txnPwDialog.mode === "custom") {
                  if (txnPwDialog.password.length < 8) {
                    toast.error("Password must be at least 8 characters.");
                    return;
                  }
                  if (txnPwDialog.password !== txnPwDialog.confirm) {
                    toast.error("Passwords do not match.");
                    return;
                  }
                }
                setTxnPwSaving(true);
                try {
                  const result = await setTransactionPassword(
                    externalAppId,
                    txnPwDialog.mode === "custom"
                      ? txnPwDialog.password
                      : undefined
                  );
                  setTxnPwResult(result);
                  toast.success(
                    result?.rotated
                      ? "Transaction password rotated."
                      : "Transaction password set."
                  );
                } catch (err) {
                  toast.error(err.response?.data?.message || "Update failed.");
                } finally {
                  setTxnPwSaving(false);
                }
              }}
            >
              {txnPwSaving ? "Saving…" : "Confirm"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={apiSecretDialog.open}
        onClose={() => {
          if (!apiSecretSaving) {
            setApiSecretDialog({ ...apiSecretDialog, open: false });
            setApiSecretResult(null);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {apiSecretResult ? "API secret updated" : "Set / rotate API secret"}
        </DialogTitle>
        <DialogContent>
          {apiSecretResult ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">
                Copy this secret now — it will not be shown again. Existing agent
                integrations must update <code>X-Api-Secret</code>.
              </Alert>
              {apiSecretResult.api_key && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    API key (unchanged)
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {apiSecretResult.api_key}
                  </Typography>
                </Box>
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  sx={{ flex: 1, wordBreak: "break-all" }}
                >
                  {apiSecretResult.api_secret}
                </Typography>
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        apiSecretResult.api_secret
                      );
                      toast.success("Copied.");
                    } catch {
                      toast.error("Could not copy.");
                    }
                  }}
                >
                  Copy
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Mode"
                value={apiSecretDialog.mode}
                onChange={(e) =>
                  setApiSecretDialog({
                    ...apiSecretDialog,
                    mode: e.target.value,
                    secret: "",
                    confirm: "",
                  })
                }
                SelectProps={{ native: true }}
              >
                <option value="generate">Auto-generate secure secret</option>
                <option value="custom">Set a custom secret</option>
              </TextField>
              {apiSecretDialog.mode === "custom" && (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="New secret (min 16 chars)"
                    value={apiSecretDialog.secret}
                    onChange={(e) =>
                      setApiSecretDialog({
                        ...apiSecretDialog,
                        secret: e.target.value,
                      })
                    }
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Confirm secret"
                    value={apiSecretDialog.confirm}
                    onChange={(e) =>
                      setApiSecretDialog({
                        ...apiSecretDialog,
                        confirm: e.target.value,
                      })
                    }
                  />
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={apiSecretSaving}
            onClick={() => {
              setApiSecretDialog({ ...apiSecretDialog, open: false });
              setApiSecretResult(null);
            }}
          >
            {apiSecretResult ? "Close" : "Cancel"}
          </Button>
          {!apiSecretResult && (
            <Button
              variant="contained"
              disabled={apiSecretSaving}
              onClick={async () => {
                if (!externalAppId) {
                  toast.error("External application id missing.");
                  return;
                }
                if (apiSecretDialog.mode === "custom") {
                  if (apiSecretDialog.secret.length < 16) {
                    toast.error("Secret must be at least 16 characters.");
                    return;
                  }
                  if (apiSecretDialog.secret !== apiSecretDialog.confirm) {
                    toast.error("Secrets do not match.");
                    return;
                  }
                }
                setApiSecretSaving(true);
                try {
                  const result = await setApiSecret(
                    externalAppId,
                    apiSecretDialog.mode === "custom"
                      ? apiSecretDialog.secret
                      : undefined
                  );
                  setApiSecretResult(result);
                  toast.success(
                    result?.rotated ? "API secret rotated." : "API secret set."
                  );
                } catch (err) {
                  toast.error(err.response?.data?.message || "Update failed.");
                } finally {
                  setApiSecretSaving(false);
                }
              }}
            >
              {apiSecretSaving ? "Saving…" : "Confirm"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
